import * as pc from 'playcanvas';
import { createMaterial, createGlowMaterial, createMetalMaterial } from './materials.js';
import { applyDifficultyToPlayer } from '../config/difficulty.js';
import { getStyleConfig, getPaletteConfig, DEFAULT_CUSTOMIZATION } from '../config/customization.js';

// Convert palette color object to pc.Color
function toColor(colorObj) {
    return new pc.Color(colorObj.r, colorObj.g, colorObj.b);
}

// Create a player entity with customization
export function createPlayer(app, playerNumber = 1, difficulty = 'normal', customization = null) {
    // Use provided customization or defaults
    const config = customization || DEFAULT_CUSTOMIZATION[playerNumber];
    const style = getStyleConfig(config.style);
    const palette = getPaletteConfig(config.palette);

    const player = new pc.Entity(`Player${playerNumber}`);

    // Create colors from palette
    const colors = {
        body: toColor(palette.primary),
        belly: toColor(palette.secondary),
        accent: toColor(palette.accent),
        dark: toColor(palette.dark)
    };

    // Create body material based on style
    let bodyMaterial;
    if (style.id === 'robot') {
        bodyMaterial = createMetalMaterial(colors.body);
    } else if (style.id === 'knight') {
        bodyMaterial = createMetalMaterial(colors.body);
    } else if (style.id === 'slime') {
        // Shiny translucent look for slime
        bodyMaterial = createMaterial(colors.body, 0.0, 0.95);
    } else {
        bodyMaterial = createMaterial(colors.body, 0.1, 0.8);
    }

    // All characters use sphere body for round, cute look
    player.addComponent('render', {
        type: 'sphere',
        material: bodyMaterial
    });

    // Spawn position based on player number
    const spawnZ = playerNumber === 1 ? 5 : 6;
    const spawnX = playerNumber === 1 ? -1 : 1;
    player.setPosition(spawnX, 0.6, spawnZ);
    player.setLocalScale(style.body.scale.x, style.body.scale.y, style.body.scale.z);
    app.root.addChild(player);

    // Add features based on style
    const features = style.features;

    // Belly (lighter colored front)
    if (features.belly) {
        createBelly(player, colors);
    }

    // Eyes - all characters have eyes
    createEyes(player, colors, style);

    // Cheeks
    if (features.cheeks) {
        createCheeks(player, colors);
    }

    // Ears
    if (features.ears) {
        createEars(player, colors, features.ears);
    }

    // Tail
    if (features.tail) {
        createTail(player, colors, features.tail);
    }

    // Horns (dragon)
    if (features.horns) {
        createHorns(player, colors);
    }

    // Wings (dragon)
    if (features.wings) {
        createWings(player, colors);
    }

    // Spikes (dragon)
    if (features.spikes) {
        createSpikes(player, colors);
    }

    // Helmet (knight)
    if (features.helmet) {
        createHelmet(player, colors, features);
    }

    // Visor without helmet (robot)
    if (features.visor && !features.helmet) {
        createRobotVisor(player, colors);
    }

    // Antenna (robot)
    if (features.antenna) {
        createAntenna(player, colors);
    }

    // Panels (robot)
    if (features.panels) {
        createPanels(player, colors);
    }

    // Whiskers (cat)
    if (features.whiskers) {
        createWhiskers(player, colors);
    }

    // Nose (cat)
    if (features.nose) {
        createNose(player, colors);
    }

    // Droplets (slime)
    if (features.droplets) {
        createDroplets(player, colors);
    }

    // Shine (slime)
    if (features.shine) {
        createShine(player);
    }

    // Player properties
    const maxHealth = applyDifficultyToPlayer(100, difficulty);
    player.velocity = new pc.Vec3(0, 0, 0);
    player.isJumping = false;
    player.health = maxHealth;
    player.maxHealth = maxHealth;
    player.isInvincible = false;
    player.invincibleTimer = 0;
    player.jumpPressed = false;
    player.attackPressed = false;
    player.dodgePressed = false;
    player.isAttacking = false;
    player.isDodging = false;
    player.baseScale = new pc.Vec3(style.body.scale.x, style.body.scale.y, style.body.scale.z);
    player.playerNumber = playerNumber;
    player.customization = config;

    console.log(`Player ${playerNumber} created: ${style.name} (${palette.name})`);
    return player;
}

// Create belly
function createBelly(player, colors) {
    const belly = new pc.Entity('Belly');
    belly.addComponent('render', {
        type: 'sphere',
        material: createMaterial(colors.belly, 0.0, 0.7)
    });
    belly.setLocalPosition(0, -0.1, -0.35);
    belly.setLocalScale(0.7, 0.7, 0.3);
    player.addChild(belly);
}

// Create eyes for a player
function createEyes(player, colors, style) {
    const eyeWhite = createMaterial(new pc.Color(1, 1, 1), 0.0, 0.9);
    const eyeBlack = createMaterial(new pc.Color(0.05, 0.05, 0.1), 0.0, 0.95);
    const eyeShine = createGlowMaterial(new pc.Color(1, 1, 1), 1.0);

    // Robot has LED eyes
    if (style.id === 'robot') {
        const ledMaterial = createGlowMaterial(colors.accent, 1.5);

        const leftEye = new pc.Entity('LeftEye');
        leftEye.addComponent('render', { type: 'sphere', material: ledMaterial });
        leftEye.setLocalPosition(-0.2, 0.15, -0.42);
        leftEye.setLocalScale(0.18, 0.22, 0.1);
        player.addChild(leftEye);

        const rightEye = new pc.Entity('RightEye');
        rightEye.addComponent('render', { type: 'sphere', material: ledMaterial });
        rightEye.setLocalPosition(0.2, 0.15, -0.42);
        rightEye.setLocalScale(0.18, 0.22, 0.1);
        player.addChild(rightEye);
        return;
    }

    // Knight has eyes visible through visor slit
    if (style.id === 'knight') {
        const leftEye = new pc.Entity('LeftEye');
        leftEye.addComponent('render', { type: 'sphere', material: eyeWhite });
        leftEye.setLocalPosition(-0.12, 0.15, -0.48);
        leftEye.setLocalScale(0.12, 0.1, 0.05);
        player.addChild(leftEye);

        const leftPupil = new pc.Entity('LeftPupil');
        leftPupil.addComponent('render', { type: 'sphere', material: eyeBlack });
        leftPupil.setLocalPosition(0, 0, -0.4);
        leftPupil.setLocalScale(0.5, 0.6, 0.5);
        leftEye.addChild(leftPupil);

        const rightEye = new pc.Entity('RightEye');
        rightEye.addComponent('render', { type: 'sphere', material: eyeWhite });
        rightEye.setLocalPosition(0.12, 0.15, -0.48);
        rightEye.setLocalScale(0.12, 0.1, 0.05);
        player.addChild(rightEye);

        const rightPupil = new pc.Entity('RightPupil');
        rightPupil.addComponent('render', { type: 'sphere', material: eyeBlack });
        rightPupil.setLocalPosition(0, 0, -0.4);
        rightPupil.setLocalScale(0.5, 0.6, 0.5);
        rightEye.addChild(rightPupil);
        return;
    }

    // Standard cute eyes for other characters
    // Left eye
    const leftEye = new pc.Entity('LeftEye');
    leftEye.addComponent('render', { type: 'sphere', material: eyeWhite });
    leftEye.setLocalPosition(-0.25, 0.15, -0.4);
    leftEye.setLocalScale(0.35, 0.4, 0.2);
    player.addChild(leftEye);

    const leftPupil = new pc.Entity('LeftPupil');
    leftPupil.addComponent('render', { type: 'sphere', material: eyeBlack });
    leftPupil.setLocalPosition(0, 0, -0.35);
    leftPupil.setLocalScale(0.6, 0.65, 0.5);
    leftEye.addChild(leftPupil);

    const leftShine = new pc.Entity('LeftShine');
    leftShine.addComponent('render', { type: 'sphere', material: eyeShine });
    leftShine.setLocalPosition(0.15, 0.15, -0.5);
    leftShine.setLocalScale(0.2, 0.2, 0.1);
    leftEye.addChild(leftShine);

    // Right eye
    const rightEye = new pc.Entity('RightEye');
    rightEye.addComponent('render', { type: 'sphere', material: eyeWhite });
    rightEye.setLocalPosition(0.25, 0.15, -0.4);
    rightEye.setLocalScale(0.35, 0.4, 0.2);
    player.addChild(rightEye);

    const rightPupil = new pc.Entity('RightPupil');
    rightPupil.addComponent('render', { type: 'sphere', material: eyeBlack });
    rightPupil.setLocalPosition(0, 0, -0.35);
    rightPupil.setLocalScale(0.6, 0.65, 0.5);
    rightEye.addChild(rightPupil);

    const rightShine = new pc.Entity('RightShine');
    rightShine.addComponent('render', { type: 'sphere', material: eyeShine });
    rightShine.setLocalPosition(0.15, 0.15, -0.5);
    rightShine.setLocalScale(0.2, 0.2, 0.1);
    rightEye.addChild(rightShine);
}

// Create cheeks
function createCheeks(player, colors) {
    const cheekMaterial = createMaterial(colors.accent, 0.0, 0.6);

    const leftCheek = new pc.Entity('LeftCheek');
    leftCheek.addComponent('render', { type: 'sphere', material: cheekMaterial });
    leftCheek.setLocalPosition(-0.4, -0.05, -0.3);
    leftCheek.setLocalScale(0.2, 0.15, 0.1);
    player.addChild(leftCheek);

    const rightCheek = new pc.Entity('RightCheek');
    rightCheek.addComponent('render', { type: 'sphere', material: cheekMaterial });
    rightCheek.setLocalPosition(0.4, -0.05, -0.3);
    rightCheek.setLocalScale(0.2, 0.15, 0.1);
    player.addChild(rightCheek);
}

// Create ears
function createEars(player, colors, earConfig) {
    const earMaterial = createMaterial(colors.body, 0.1, 0.8);
    const earTipMaterial = createMaterial(colors.dark, 0.2, 0.7);
    const innerEarMaterial = createMaterial(colors.accent, 0.0, 0.6);

    const type = earConfig.type || 'pointed';
    const angle = earConfig.angle || 25;

    if (type === 'cat') {
        // Triangle cat ears
        const leftEar = new pc.Entity('LeftEar');
        leftEar.addComponent('render', { type: 'cone', material: earMaterial });
        leftEar.setLocalPosition(-0.3, 0.5, 0);
        leftEar.setLocalScale(0.25, 0.35, 0.15);
        leftEar.setLocalEulerAngles(0, 0, angle);
        player.addChild(leftEar);

        const leftInner = new pc.Entity('LeftInner');
        leftInner.addComponent('render', { type: 'cone', material: innerEarMaterial });
        leftInner.setLocalPosition(0, 0.1, -0.15);
        leftInner.setLocalScale(0.5, 0.5, 0.3);
        leftEar.addChild(leftInner);

        const rightEar = new pc.Entity('RightEar');
        rightEar.addComponent('render', { type: 'cone', material: earMaterial });
        rightEar.setLocalPosition(0.3, 0.5, 0);
        rightEar.setLocalScale(0.25, 0.35, 0.15);
        rightEar.setLocalEulerAngles(0, 0, -angle);
        player.addChild(rightEar);

        const rightInner = new pc.Entity('RightInner');
        rightInner.addComponent('render', { type: 'cone', material: innerEarMaterial });
        rightInner.setLocalPosition(0, 0.1, -0.15);
        rightInner.setLocalScale(0.5, 0.5, 0.3);
        rightEar.addChild(rightInner);
    } else {
        // Pointed ears (creature default)
        const leftEar = new pc.Entity('LeftEar');
        leftEar.addComponent('render', { type: 'cone', material: earMaterial });
        leftEar.setLocalPosition(-0.35, 0.55, 0);
        leftEar.setLocalScale(0.2, 0.5, 0.15);
        leftEar.setLocalEulerAngles(0, 0, angle);
        player.addChild(leftEar);

        const leftEarTip = new pc.Entity('LeftEarTip');
        leftEarTip.addComponent('render', { type: 'cone', material: earTipMaterial });
        leftEarTip.setLocalPosition(0, 0.35, 0);
        leftEarTip.setLocalScale(0.7, 0.4, 0.7);
        leftEar.addChild(leftEarTip);

        const rightEar = new pc.Entity('RightEar');
        rightEar.addComponent('render', { type: 'cone', material: earMaterial });
        rightEar.setLocalPosition(0.35, 0.55, 0);
        rightEar.setLocalScale(0.2, 0.5, 0.15);
        rightEar.setLocalEulerAngles(0, 0, -angle);
        player.addChild(rightEar);

        const rightEarTip = new pc.Entity('RightEarTip');
        rightEarTip.addComponent('render', { type: 'cone', material: earTipMaterial });
        rightEarTip.setLocalPosition(0, 0.35, 0);
        rightEarTip.setLocalScale(0.7, 0.4, 0.7);
        rightEar.addChild(rightEarTip);
    }
}

// Create tail
function createTail(player, colors, tailConfig) {
    const tailMaterial = createMaterial(colors.body, 0.1, 0.8);
    const type = tailConfig.type || 'round';

    if (type === 'long') {
        // Long cat-style tail using multiple segments
        const tail1 = new pc.Entity('Tail1');
        tail1.addComponent('render', { type: 'capsule', material: tailMaterial });
        tail1.setLocalPosition(0, 0, 0.5);
        tail1.setLocalScale(0.12, 0.2, 0.12);
        tail1.setLocalEulerAngles(50, 0, 0);
        player.addChild(tail1);

        const tail2 = new pc.Entity('Tail2');
        tail2.addComponent('render', { type: 'capsule', material: tailMaterial });
        tail2.setLocalPosition(0, 0.35, 0);
        tail2.setLocalScale(0.85, 0.8, 0.85);
        tail2.setLocalEulerAngles(-40, 0, 0);
        tail1.addChild(tail2);

        const tailTip = new pc.Entity('TailTip');
        tailTip.addComponent('render', { type: 'sphere', material: createMaterial(colors.dark, 0.1, 0.8) });
        tailTip.setLocalPosition(0, 0.35, 0);
        tailTip.setLocalScale(1.3, 1.0, 1.3);
        tail2.addChild(tailTip);
    } else if (type === 'spiked') {
        // Dragon-style spiked tail
        const tail = new pc.Entity('Tail');
        tail.addComponent('render', { type: 'cone', material: tailMaterial });
        tail.setLocalPosition(0, -0.1, 0.55);
        tail.setLocalScale(0.18, 0.45, 0.18);
        tail.setLocalEulerAngles(100, 0, 0);
        player.addChild(tail);

        // Spikes on tail
        const spikeMaterial = createMaterial(colors.accent, 0.2, 0.7);
        for (let i = 0; i < 3; i++) {
            const spike = new pc.Entity(`TailSpike${i}`);
            spike.addComponent('render', { type: 'cone', material: spikeMaterial });
            spike.setLocalPosition(0, 0.12 + i * 0.12, 0);
            spike.setLocalScale(0.35, 0.25, 0.35);
            tail.addChild(spike);
        }
    } else {
        // Round puffy tail (default)
        const tail = new pc.Entity('Tail');
        tail.addComponent('render', { type: 'sphere', material: tailMaterial });
        tail.setLocalPosition(0, -0.1, 0.5);
        tail.setLocalScale(0.25, 0.2, 0.18);
        player.addChild(tail);
    }
}

// Create horns (dragon)
function createHorns(player, colors) {
    const hornMaterial = createMaterial(colors.dark, 0.3, 0.8);

    const leftHorn = new pc.Entity('LeftHorn');
    leftHorn.addComponent('render', { type: 'cone', material: hornMaterial });
    leftHorn.setLocalPosition(-0.28, 0.42, 0.1);
    leftHorn.setLocalScale(0.1, 0.35, 0.1);
    leftHorn.setLocalEulerAngles(-15, 0, 20);
    player.addChild(leftHorn);

    const rightHorn = new pc.Entity('RightHorn');
    rightHorn.addComponent('render', { type: 'cone', material: hornMaterial });
    rightHorn.setLocalPosition(0.28, 0.42, 0.1);
    rightHorn.setLocalScale(0.1, 0.35, 0.1);
    rightHorn.setLocalEulerAngles(-15, 0, -20);
    player.addChild(rightHorn);
}

// Create wings (dragon)
function createWings(player, colors) {
    const wingMaterial = createMaterial(colors.accent, 0.0, 0.6);
    const wingBoneMaterial = createMaterial(colors.body, 0.1, 0.8);

    // Left wing
    const leftWing = new pc.Entity('LeftWing');
    leftWing.setLocalPosition(-0.48, 0.1, 0.15);
    leftWing.setLocalEulerAngles(0, -25, 15);
    player.addChild(leftWing);

    const leftBone = new pc.Entity('LeftBone');
    leftBone.addComponent('render', { type: 'capsule', material: wingBoneMaterial });
    leftBone.setLocalPosition(-0.12, 0.12, 0);
    leftBone.setLocalScale(0.05, 0.18, 0.05);
    leftBone.setLocalEulerAngles(0, 0, 40);
    leftWing.addChild(leftBone);

    const leftMembrane = new pc.Entity('LeftMembrane');
    leftMembrane.addComponent('render', { type: 'cone', material: wingMaterial });
    leftMembrane.setLocalPosition(-0.18, 0, 0);
    leftMembrane.setLocalScale(0.22, 0.02, 0.28);
    leftMembrane.setLocalEulerAngles(0, 0, 90);
    leftWing.addChild(leftMembrane);

    // Right wing
    const rightWing = new pc.Entity('RightWing');
    rightWing.setLocalPosition(0.48, 0.1, 0.15);
    rightWing.setLocalEulerAngles(0, 25, -15);
    player.addChild(rightWing);

    const rightBone = new pc.Entity('RightBone');
    rightBone.addComponent('render', { type: 'capsule', material: wingBoneMaterial });
    rightBone.setLocalPosition(0.12, 0.12, 0);
    rightBone.setLocalScale(0.05, 0.18, 0.05);
    rightBone.setLocalEulerAngles(0, 0, -40);
    rightWing.addChild(rightBone);

    const rightMembrane = new pc.Entity('RightMembrane');
    rightMembrane.addComponent('render', { type: 'cone', material: wingMaterial });
    rightMembrane.setLocalPosition(0.18, 0, 0);
    rightMembrane.setLocalScale(0.22, 0.02, 0.28);
    rightMembrane.setLocalEulerAngles(0, 0, -90);
    rightWing.addChild(rightMembrane);
}

// Create spikes (dragon)
function createSpikes(player, colors) {
    const spikeMaterial = createMaterial(colors.accent, 0.2, 0.7);

    // Back spikes running down the spine
    for (let i = 0; i < 3; i++) {
        const spike = new pc.Entity(`BackSpike${i}`);
        spike.addComponent('render', { type: 'cone', material: spikeMaterial });
        spike.setLocalPosition(0, 0.38 - i * 0.18, 0.15 + i * 0.12);
        spike.setLocalScale(0.08, 0.18 - i * 0.03, 0.08);
        player.addChild(spike);
    }
}

// Create helmet (knight)
function createHelmet(player, colors, features) {
    const metalMaterial = createMetalMaterial(colors.body);
    const darkMaterial = createMaterial(colors.dark, 0.5, 0.9);

    // Helmet dome on top
    const helmet = new pc.Entity('Helmet');
    helmet.addComponent('render', { type: 'sphere', material: metalMaterial });
    helmet.setLocalPosition(0, 0.32, 0);
    helmet.setLocalScale(0.9, 0.55, 0.85);
    player.addChild(helmet);

    // Visor slit
    if (features.visor) {
        const visor = new pc.Entity('Visor');
        visor.addComponent('render', { type: 'sphere', material: darkMaterial });
        visor.setLocalPosition(0, 0.15, -0.46);
        visor.setLocalScale(0.45, 0.1, 0.08);
        player.addChild(visor);
    }

    // Plume on top
    if (features.plume) {
        const plume = new pc.Entity('Plume');
        plume.addComponent('render', { type: 'capsule', material: createMaterial(colors.accent, 0.0, 0.6) });
        plume.setLocalPosition(0, 0.6, 0.08);
        plume.setLocalScale(0.08, 0.22, 0.08);
        plume.setLocalEulerAngles(15, 0, 0);
        player.addChild(plume);
    }
}

// Create robot visor
function createRobotVisor(player, colors) {
    const visorMaterial = createGlowMaterial(colors.accent, 0.6);

    const visor = new pc.Entity('Visor');
    visor.addComponent('render', { type: 'sphere', material: visorMaterial });
    visor.setLocalPosition(0, 0.08, -0.44);
    visor.setLocalScale(0.5, 0.12, 0.08);
    player.addChild(visor);
}

// Create antenna (robot)
function createAntenna(player, colors) {
    const antennaMaterial = createMetalMaterial(colors.dark);
    const tipMaterial = createGlowMaterial(colors.accent, 1.5);

    const antenna = new pc.Entity('Antenna');
    antenna.addComponent('render', { type: 'cylinder', material: antennaMaterial });
    antenna.setLocalPosition(0, 0.55, 0);
    antenna.setLocalScale(0.04, 0.18, 0.04);
    player.addChild(antenna);

    const tip = new pc.Entity('AntennaTip');
    tip.addComponent('render', { type: 'sphere', material: tipMaterial });
    tip.setLocalPosition(0, 0.55, 0);
    tip.setLocalScale(0.12, 0.12, 0.12);
    antenna.addChild(tip);
}

// Create panels (robot)
function createPanels(player, colors) {
    const panelMaterial = createMaterial(colors.dark, 0.6, 0.8);

    // Side panels
    const leftPanel = new pc.Entity('LeftPanel');
    leftPanel.addComponent('render', { type: 'sphere', material: panelMaterial });
    leftPanel.setLocalPosition(-0.42, 0, 0);
    leftPanel.setLocalScale(0.12, 0.25, 0.2);
    player.addChild(leftPanel);

    const rightPanel = new pc.Entity('RightPanel');
    rightPanel.addComponent('render', { type: 'sphere', material: panelMaterial });
    rightPanel.setLocalPosition(0.42, 0, 0);
    rightPanel.setLocalScale(0.12, 0.25, 0.2);
    player.addChild(rightPanel);

    // Chest indicator
    const chest = new pc.Entity('ChestLight');
    chest.addComponent('render', { type: 'sphere', material: createGlowMaterial(colors.accent, 0.8) });
    chest.setLocalPosition(0, -0.05, -0.45);
    chest.setLocalScale(0.12, 0.12, 0.06);
    player.addChild(chest);
}

// Create whiskers (cat)
function createWhiskers(player, colors) {
    const whiskerMaterial = createMaterial(new pc.Color(0.15, 0.15, 0.15), 0.0, 0.5);

    // Left whiskers
    for (let i = 0; i < 3; i++) {
        const whisker = new pc.Entity(`LeftWhisker${i}`);
        whisker.addComponent('render', { type: 'cylinder', material: whiskerMaterial });
        whisker.setLocalPosition(-0.38, -0.02 + (i - 1) * 0.07, -0.32);
        whisker.setLocalScale(0.012, 0.14, 0.012);
        whisker.setLocalEulerAngles(0, 0, 82 + (i - 1) * 8);
        player.addChild(whisker);
    }

    // Right whiskers
    for (let i = 0; i < 3; i++) {
        const whisker = new pc.Entity(`RightWhisker${i}`);
        whisker.addComponent('render', { type: 'cylinder', material: whiskerMaterial });
        whisker.setLocalPosition(0.38, -0.02 + (i - 1) * 0.07, -0.32);
        whisker.setLocalScale(0.012, 0.14, 0.012);
        whisker.setLocalEulerAngles(0, 0, -82 - (i - 1) * 8);
        player.addChild(whisker);
    }
}

// Create nose (cat)
function createNose(player, colors) {
    const noseMaterial = createMaterial(colors.accent, 0.0, 0.85);

    const nose = new pc.Entity('Nose');
    nose.addComponent('render', { type: 'sphere', material: noseMaterial });
    nose.setLocalPosition(0, -0.03, -0.48);
    nose.setLocalScale(0.1, 0.07, 0.07);
    player.addChild(nose);
}

// Create droplets (slime)
function createDroplets(player, colors) {
    const dropMaterial = createMaterial(colors.body, 0.0, 0.95);

    // Droplets dripping down
    const positions = [
        { x: -0.38, y: -0.32, z: 0.05 },
        { x: 0.32, y: -0.38, z: 0.12 },
        { x: 0.05, y: -0.42, z: 0.25 },
        { x: -0.2, y: -0.4, z: -0.15 }
    ];

    positions.forEach((pos, i) => {
        const drop = new pc.Entity(`Droplet${i}`);
        drop.addComponent('render', { type: 'sphere', material: dropMaterial });
        drop.setLocalPosition(pos.x, pos.y, pos.z);
        drop.setLocalScale(0.1, 0.16, 0.1);
        player.addChild(drop);
    });
}

// Create shine (slime)
function createShine(player) {
    const shineMaterial = createGlowMaterial(new pc.Color(1, 1, 1), 0.4);

    const shine = new pc.Entity('SlimeShine');
    shine.addComponent('render', { type: 'sphere', material: shineMaterial });
    shine.setLocalPosition(-0.18, 0.22, -0.28);
    shine.setLocalScale(0.18, 0.12, 0.1);
    player.addChild(shine);

    const shine2 = new pc.Entity('SlimeShine2');
    shine2.addComponent('render', { type: 'sphere', material: shineMaterial });
    shine2.setLocalPosition(-0.08, 0.3, -0.2);
    shine2.setLocalScale(0.08, 0.06, 0.05);
    player.addChild(shine2);
}

// Reset player to starting state
export function resetPlayer(player, difficulty = 'normal') {
    const maxHealth = applyDifficultyToPlayer(100, difficulty);
    const spawnZ = player.playerNumber === 1 ? 5 : 6;
    const spawnX = player.playerNumber === 1 ? -1 : 1;

    player.setPosition(spawnX, 0.6, spawnZ);
    player.health = maxHealth;
    player.maxHealth = maxHealth;
    player.isInvincible = false;
    player.invincibleTimer = 0;
    player.enabled = true;
    player.setLocalScale(player.baseScale.x, player.baseScale.y, player.baseScale.z);
    player.velocity = new pc.Vec3(0, 0, 0);
    player.isJumping = false;
    player.isAttacking = false;
    player.isDodging = false;
}

// Check if all players are defeated
export function areAllPlayersDefeated(players) {
    return players.every(p => p.health <= 0);
}

// Get living players
export function getLivingPlayers(players) {
    return players.filter(p => p.health > 0);
}
